import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  AlertCircle,
  CalendarDays,
  Check,
  Clock,
  CreditCard,
  Loader2,
  Lock,
  MapPin,
  ShieldCheck,
  User,
} from 'lucide-react'

import apiClient from '@/api/axios'
import { API_ENDPOINTS } from '@/api/endpoints'

import { getCountryName } from '@/lib/countries'
import { formatDate, fromUTC, now } from '@/lib/date'
import { logger } from '@/lib/logger'
import { formatMoney } from '@/lib/money'
import { calculateEndDate, cn } from '@/lib/utils'

import { useAuthStore } from '@/stores/auth'
import { useAppSettings, useSetting } from '@/stores/useAppSettings'

import { paths } from '@/config/paths'

import toast from '@/components/toast'
import { SmartButton } from '@/components/ui-custom/SmartButton'

import { PaymentStatus } from '@/features/payments/types/payment.types'
import { PlanDuration, PlanType } from '@/features/plans/types/plan.types'

interface Branch {
  id: number
  name: string
  address?: string
  city?: string
  country?: string
}

interface Plan {
  id: number
  branch_id?: number | null
  name: string
  description?: string
  price: number
  currency: string
  duration_value?: number | null
  duration_unit?: PlanDuration
  plan_type: PlanType
  is_active: boolean
  is_global: boolean
  created_at: string
  updated_at: string
}

interface AccountData {
  first_name: string
  last_name: string
  email: string
  phone: string
  civil_id?: string
  country: string
  city?: string
}

interface UniversalCheckoutFormProps {
  branch: Branch
  plan: Plan
  accountData: AccountData
  mode: 'registration' | 'upgrade' // Determines which API endpoint to use
  sessionId?: string | null // Required for registration
  onSuccess?: () => void
}

interface PaymentMethod {
  id: string
  label: string
  icon?: React.ReactNode
}

export function UniversalCheckoutForm({
  branch,
  plan,
  accountData,
  mode,
  sessionId,
  onSuccess,
}: UniversalCheckoutFormProps) {
  const { t, i18n } = useTranslation(['auth', 'common'])
  const isAuthenticated = useAuthStore((state) => !!state.user)
  const { getSetting } = useAppSettings()
  const paymentEnabled = useSetting<boolean>('payments.payment_enabled', true)
  const supportUrl = getSetting('general.support_url', '')

  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('none')
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isChecking, setIsChecking] = useState(true) // Check for both modes

  // Get current locale for country name translation
  const currentLocale = i18n.language || 'en'

  // Check for existing payment on mount
  useEffect(() => {
    let isMounted = true

    const checkExistingPayment = async () => {
      try {
        // Audit: record that we are checking for existing payment (no sensitive data)
        logger.info('Checking existing payment', {
          mode,
          sessionId: mode === 'registration' ? !!sessionId : undefined,
          branchId: branch?.id,
          planId: plan?.id,
          isAuthenticated: isAuthenticated,
        })
        // Use different endpoint based on mode
        const endpoint =
          mode === 'registration' ? API_ENDPOINTS.register.checkStatus : API_ENDPOINTS.memberPayments.checkStatus

        const payload = mode === 'registration' ? { session_id: sessionId } : {} // Member endpoint doesn't need any payload (uses authenticated user)

        const response = await apiClient.post(endpoint, payload)
        logger.debug(endpoint, response.data)
        if (!isMounted) return

        const { status, payment_url, expires_at } = response.data

        // Check if payment is expired
        if (status === 'processing') {
          if (expires_at && fromUTC(expires_at) && fromUTC(expires_at)! < now()) {
            setPaymentStatus('expired')
          } else {
            setPaymentStatus(status)
            setPaymentUrl(payment_url)
          }
        } else if (status === 'pending') {
          setPaymentStatus(status)
        } else if (status === 'paid' || status === 'completed') {
          setPaymentStatus('paid')
          // Call onSuccess callback
          if (onSuccess) {
            onSuccess()
          }
        } else {
          setPaymentStatus(status)
        }
        // Audit: record observed payment status (do NOT include full URLs)
        logger.info(
          'Observed payment status',
          {
            status,
            hasPaymentUrl: !!payment_url,
            branchId: branch?.id,
            planId: plan?.id,
            isAuthenticated: isAuthenticated,
          },
          { sendToSentry: true }
        )
      } catch (error) {
        if (!isMounted) return
        logger.error('Failed to check payment status', error)
        logger.info('Check existing payment failed', {
          branchId: branch?.id,
          planId: plan?.id,
          isAuthenticated: isAuthenticated,
        })
        setPaymentStatus('none')
      } finally {
        if (isMounted) {
          setIsChecking(false)
        }
      }
    }

    // Only check if we have sessionId for registration or if it's upgrade mode
    if (mode === 'registration' && !sessionId) {
      setIsChecking(false)
      return
    }

    checkExistingPayment()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'myfatoorah',
      label: t('auth:paymentMethods.myfatoorah', 'MyFatoorah'),
      icon: <CreditCard className='h-6 w-6 text-gray-700' />,
    },
  ]

  const handleInitiatePayment = async () => {
    if (!selectedMethod) {
      toast.error(t('auth:checkoutForm.errors.selectPaymentMethod', 'Please select a payment method'))
      return
    }

    if (mode === 'registration' && !sessionId) {
      toast.error(t('auth:checkoutForm.errors.invalidSession', 'Invalid session. Please try registering again.'))
      return
    }

    setIsProcessing(true)
    // Audit: checkout initiated (non-sensitive)
    logger.info(
      'Checkout initiated',
      {
        isAuthenticated: isAuthenticated,
        planId: plan?.id,
        branchId: branch?.id,
        mode,
        paymentMethod: selectedMethod,
      },
      { sendToSentry: true }
    )
    try {
      const payload: any = {
        payment_method: selectedMethod,
        plan_id: plan?.id,
      }

      // Add session_id and branch_id only for registration
      if (mode === 'registration' && sessionId) {
        payload.session_id = sessionId
        payload.branch_id = branch?.id
      }

      const endpoint = mode === 'registration' ? API_ENDPOINTS.payments.initiate : API_ENDPOINTS.memberPayments.initiate

      const response = await apiClient.post(endpoint, payload)
      logger.debug(endpoint, response.data)

      const isFreeplan = response?.data?.data?.data?.is_free_plan
      const newPaymentUrl = response?.data?.data?.data?.payment_url

      if (isFreeplan) {
        // Free plan - redirect to success page
        logger.info(
          'Checkout completed (free plan)',
          {
            isAuthenticated: isAuthenticated,
            planId: plan?.id,
            branchId: branch?.id,
          },
          { sendToSentry: true }
        )
        window.location.href = `${paths.public.paymentResult.getHref()}?s=success`
      } else if (newPaymentUrl) {
        // Paid plan - redirect to payment gateway (do NOT log the full URL)
        logger.info(
          'Redirecting to payment gateway',
          {
            isAuthenticated: isAuthenticated,
            planId: plan?.id,
            branchId: branch?.id,
            hasPaymentUrl: !!newPaymentUrl,
          },
          { sendToSentry: true }
        )
        window.location.href = newPaymentUrl
      }
    } catch (error: any) {
      logger.error('Payment initiation failed', error)
      logger.info(
        'Checkout initiation failed',
        {
          isAuthenticated: isAuthenticated,
          planId: plan?.id,
          branchId: branch?.id,
          status: error.response?.status,
        },
        { sendToSentry: true }
      )

      if (error.response?.status === 422) {
        const existingPayment = error.response?.data?.data
        setPaymentStatus('pending')
        setPaymentUrl(existingPayment?.payment_url)
        toast.info(
          t(
            'auth:checkoutForm.errors.paymentInProgress',
            'You already have a payment in progress. Click Resume Payment.'
          )
        )
      } else {
        const errorMessage = error.response?.data?.errorMessage
          ? error.response.data.errorMessage
          : t('auth:checkoutForm.errors.paymentFailed', 'Payment processing failed. Please try again.')
        toast.error(errorMessage)
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const handleResumePayment = () => {
    // Audit: user resumed payment flow (do not include payment URL)
    logger.info(
      'Resuming payment',
      {
        isAuthenticated: isAuthenticated,
        planId: plan?.id,
        branchId: branch?.id,
      },
      { sendToSentry: true }
    )
    if (paymentUrl) {
      window.location.href = paymentUrl
    }
  }

  const { startDate, endDate } = calculateEndDate(plan)

  const duration =
    plan.duration_value && plan.duration_unit ? `${plan.duration_value} ${plan.duration_unit}` : 'Lifetime'

  // Check if payments are disabled
  if (!paymentEnabled) {
    return (
      <div className='mx-auto w-full max-w-2xl'>
        <div className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl'>
          {/* Header with gradient */}
          <div className='bg-gradient-to-br from-red-50 to-orange-50 px-6 py-8 sm:px-8'>
            <div className='flex flex-col items-center text-center'>
              <div className='mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 ring-4 ring-red-50'>
                <AlertCircle className='h-8 w-8 text-red-600' />
              </div>
              <h2 className='text-2xl font-bold text-gray-900'>
                {t('common:errors.paymentsDisabled', 'Payments Disabled')}
              </h2>
              <p className='mt-3 max-w-md text-sm leading-relaxed text-gray-600'>
                {t(
                  'common:errors.paymentsDisabledDescription',
                  'Online payments are currently disabled. Please contact support for assistance.'
                )}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className='space-y-6 px-6 py-8 sm:px-8'>
            {/* Info cards */}
            <div className='space-y-3'>
              <div className='flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4'>
                <User className='mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600' />
                <div className='flex-1'>
                  <p className='text-sm font-medium text-gray-900'>{t('common:errors.needHelp', 'Need Help?')}</p>
                  <p className='mt-1 text-xs text-gray-600'>
                    {t(
                      'common:errors.needHelpDesc',
                      'Our support team is ready to assist you with registration and payment options.'
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className='flex flex-col gap-3 pt-2 sm:flex-row'>
              <SmartButton
                variant='default'
                size='lg'
                className='flex-1'
                onClick={() =>
                  (window.location.href = isAuthenticated ? paths.dashboard.support.root.getHref() : supportUrl)
                }
              >
                {t('common:actions.contactSupport', 'Contact Support')}
              </SmartButton>
              <SmartButton variant='outline' size='lg' className='flex-1' onClick={() => (window.location.href = '/')}>
                {t('common:actions.backToHome', 'Back to Home')}
              </SmartButton>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show loading state while checking payment status
  if (isChecking) {
    return (
      <div className='mx-auto flex w-full max-w-3xl items-center justify-center rounded-2xl border border-gray-100 bg-white p-12 shadow-lg'>
        <Loader2 className='mr-3 h-8 w-8 animate-spin text-blue-600' />
        <span className='text-lg font-medium text-gray-700'>
          {t('auth:checkoutForm.checkingStatus', 'Checking payment status...')}
        </span>
      </div>
    )
  }

  return (
    <div className='mx-auto w-full max-w-3xl space-y-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-lg sm:p-8'>
      {/* Header Section */}
      <div className='flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center'>
        <div>
          <h2 className='text-2xl font-bold text-gray-900'>
            {mode === 'registration'
              ? t('auth:checkoutForm.title', 'Complete Your Purchase')
              : t('common:checkoutForm.title', 'Complete Payment')}
          </h2>
          <p className='text-muted-foreground mt-1 text-sm'>
            {t('auth:checkoutForm.subtitle', 'Review your order and payment details')}
          </p>
        </div>

        <div className='flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5'>
          <Lock className='h-4 w-4 text-blue-600' />
          <span className='text-sm font-medium text-blue-700'>
            {t('auth:checkoutForm.secureCheckoutMsg', 'Secure Checkout')}
          </span>
        </div>
      </div>

      {/* Plan Summary Card */}
      <div className='space-y-6 rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6'>
        <div className='flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center'>
          <div className='space-y-1'>
            <span className='text-xs font-semibold tracking-wider text-blue-600 uppercase'>
              {t('auth:checkoutForm.yourPlanMsg', 'Your Plan')}
            </span>
            <h3 className='text-primary text-xl font-bold'>{plan.name}</h3>
            <p className='text-xs text-gray-400'>
              {plan.description || t('auth:checkoutForm.selectedMembership', 'Selected membership plan')}
            </p>
          </div>

          <span className='inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800'>
            {t('checkout.selected', 'Selected')}
          </span>
        </div>

        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          {/* Price Section */}
          <div className='space-y-4'>
            <div className='flex items-baseline gap-2'>
              <span className='text-3xl font-bold text-blue-600'>{formatMoney(plan.price, plan.currency)}</span>
              <span className='text-sm font-medium text-gray-600'>/ {duration}</span>
            </div>

            {plan.description && (
              <div className='space-y-2'>
                <h4 className='text-sm font-semibold text-gray-800'>{t('auth:checkoutForm.details', 'Details')}</h4>
                <p className='text-sm text-gray-600'>{plan.description}</p>
              </div>
            )}
          </div>

          {/* Renewal Section */}
          <div className='space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4'>
            <div className='flex items-center gap-2'>
              <CalendarDays className='h-5 w-5 text-gray-500' />
              <h4 className='text-sm font-semibold text-gray-800'>
                {t('auth:checkoutForm.renewalDetails', 'Renewal Details')}
              </h4>
            </div>

            <div className='space-y-2 text-sm'>
              <div className='flex justify-between'>
                <span className='text-gray-600'>{t('auth:checkoutForm.startDate', 'Start Date')}:</span>
                <span className='font-medium text-gray-900'>{formatDate(startDate)}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>{t('auth:checkoutForm.nextBilling', 'Next Billing')}:</span>
                <span className='font-medium text-gray-900'>{formatDate(endDate)}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>{t('auth:checkoutForm.billingCycle', 'Billing Cycle')}:</span>
                <span className='font-medium text-gray-900'>
                  {plan.duration_value} {plan.duration_unit}
                  {plan.duration_value && plan.duration_value > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className='border-t border-gray-200 pt-8'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            {/* Branch Details */}
            <div className='rounded-xl border border-gray-100 bg-white p-5 shadow-xs'>
              <div className='mb-4 flex items-center gap-3'>
                <div className='rounded-lg bg-blue-50 p-2'>
                  <MapPin className='h-5 w-5 text-blue-600' />
                </div>
                <h4 className='text-base font-semibold text-gray-800'>
                  {t('auth:checkoutForm.selectedBranch', 'Selected Branch')}
                </h4>
              </div>

              <div className='space-y-3'>
                <DetailRow label={t('auth:checkoutForm.branch.name', 'Name')} value={branch.name} />
                <DetailRow
                  label={t('auth:checkoutForm.branch.address', 'Address')}
                  value={branch.address}
                  className='items-start'
                />
                <DetailRow label={t('auth:checkoutForm.branch.city', 'City')} value={branch.city} />
                <DetailRow
                  label={t('auth:checkoutForm.branch.country', 'Country')}
                  value={getCountryName(branch.country, currentLocale)}
                />
              </div>
            </div>

            {/* Account Info */}
            <div className='rounded-xl border border-gray-100 bg-white p-5 shadow-xs'>
              <div className='mb-4 flex items-center gap-3'>
                <div className='rounded-lg bg-blue-50 p-2'>
                  <User className='h-5 w-5 text-blue-600' />
                </div>
                <h4 className='text-base font-semibold text-gray-800'>
                  {t('auth:checkoutForm.accountInfo', 'Account Information')}
                </h4>
              </div>

              <div className='space-y-3'>
                <DetailRow
                  label={t('auth:checkoutForm.customer.name', 'Name')}
                  value={`${accountData.first_name} ${accountData.last_name}`}
                />
                <DetailRow label={t('auth:checkoutForm.customer.email', 'Email')} value={accountData.email} />
                <DetailRow label={t('auth:checkoutForm.customer.phone', 'Phone')} value={accountData.phone} />
                {accountData.civil_id && (
                  <DetailRow label={t('auth:checkoutForm.customer.civilId', 'Civil ID')} value={accountData.civil_id} />
                )}
                <DetailRow
                  label={t('auth:checkoutForm.customer.country', 'Country')}
                  value={getCountryName(accountData.country, currentLocale)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status Messages */}
      {paymentStatus === 'pending' && (
        <div className='flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm'>
          <Clock className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600' />
          <div className='flex-1'>
            <p className='font-semibold text-amber-900'>
              {t('auth:checkoutForm.status.pendingTitle', 'Payment In Progress')}
            </p>
            <p className='mt-1 text-sm text-amber-700'>
              {t('auth:checkoutForm.status.pendingMessage', "You have a payment that's currently pending.")}
            </p>
          </div>
        </div>
      )}

      {paymentStatus === 'failed' && (
        <div className='flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm'>
          <AlertCircle className='mt-0.5 h-5 w-5 flex-shrink-0 text-red-600' />
          <div className='flex-1'>
            <p className='font-semibold text-red-900'>{t('auth:checkoutForm.status.failedTitle', 'Payment Failed')}</p>
            <p className='mt-1 text-sm text-red-700'>
              {t('auth:checkoutForm.status.failedMessage', 'Your previous payment attempt failed. Please try again.')}
            </p>
          </div>
        </div>
      )}

      {paymentStatus === 'expired' && (
        <div className='flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-5 shadow-sm'>
          <AlertCircle className='mt-0.5 h-5 w-5 flex-shrink-0 text-gray-600' />
          <div className='flex-1'>
            <p className='font-semibold text-gray-900'>
              {t('auth:checkoutForm.status.expiredTitle', 'Payment Expired')}
            </p>
            <p className='mt-1 text-sm text-gray-700'>
              {t(
                'auth:checkoutForm.status.expiredMessage',
                'Your previous payment link has expired. Please start a new payment.'
              )}
            </p>
          </div>
        </div>
      )}

      {/* Payment Methods */}
      <div className='space-y-4'>
        <h2 className='text-xl font-bold text-gray-900'>{t('auth:checkoutForm.paymentMethod', 'Payment Method')}</h2>

        <div className='grid grid-cols-1 gap-3'>
          {paymentMethods.map((method) => (
            <PaymentMethodCard
              key={method.id}
              method={method}
              isSelected={selectedMethod === method.id}
              onSelect={() => setSelectedMethod(method.id)}
            />
          ))}
        </div>
      </div>

      {/* Payment Button */}
      <div className='space-y-3 pt-4'>
        {paymentStatus === 'processing' ? (
          <>
            <SmartButton
              variant='default'
              size='lg'
              className='h-12 w-full text-base font-semibold'
              disabled={!paymentUrl}
              onClick={handleResumePayment}
            >
              {t('auth:checkoutForm.resumePayment', 'Complete Payment')}
            </SmartButton>
            <p className='text-center text-sm text-gray-600'>
              {t('auth:checkoutForm.resumePaymentHint', 'Click above to continue your payment where you left off')}
            </p>
          </>
        ) : paymentStatus === 'pending' ? (
          <></>
        ) : (
          <>
            <SmartButton
              variant='default'
              size='lg'
              className='h-12 w-full text-base font-semibold'
              disabled={isProcessing || !selectedMethod}
              onClick={handleInitiatePayment}
              loading={isProcessing}
            >
              {paymentStatus === 'failed' || paymentStatus === 'expired'
                ? t('auth:checkoutForm.tryAgain', 'Try Payment Again')
                : t('auth:checkoutForm.submit', 'Complete Payment')}
            </SmartButton>
            <p className='text-center text-sm text-gray-600'>
              {t('auth:checkoutForm.redirectHint', "You'll be redirected to our secure payment gateway")}
            </p>
          </>
        )}

        <div className='flex items-center justify-center gap-2 text-sm text-gray-500'>
          <ShieldCheck className='h-4 w-4 text-green-500' />
          <span>{t('auth:checkoutForm.securePaymentMsg', 'Payments are secure and encrypted')}</span>
        </div>
      </div>
    </div>
  )
}

interface PaymentMethodCardProps {
  method: PaymentMethod
  isSelected: boolean
  onSelect: () => void
}

function PaymentMethodCard({ method, isSelected, onSelect }: PaymentMethodCardProps) {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={cn(
        'relative cursor-pointer overflow-hidden rounded-lg border p-4 text-left',
        'flex items-center gap-4 transition-all hover:border-gray-400 hover:bg-gray-50',
        isSelected ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-500/20' : 'border-gray-300'
      )}
    >
      <div
        className={cn(
          'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border',
          isSelected ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300 bg-white'
        )}
      >
        {isSelected && <Check className='h-3 w-3' />}
      </div>

      <div className='flex items-center gap-3'>
        {method.icon}
        <span className='font-medium text-gray-800'>{method.label}</span>
      </div>
    </button>
  )
}

interface DetailRowProps {
  label: string
  value: string | null | undefined
  className?: string
}

function DetailRow({ label, value, className = '' }: DetailRowProps) {
  return (
    <div className={`flex gap-1 ${className}`}>
      <span className='w-28 flex-shrink-0 text-sm font-medium text-gray-500'>{label}</span>
      <span className='text-sm font-medium break-all text-gray-800'>{value ? value : 'N/A'}</span>
    </div>
  )
}
